/**
 * OfflineBanner — mounted in AppShell, shows when offline.
 * navigator.onLine alene er upålidelig: værdien fanget ved mount kan være
 * forældet (netværksskifte under load → 'online'-eventet affyres før
 * lytterne er på), og Chrome kan melde falsk offline ved VPN/adapter-skift.
 * Derfor bekræftes offline-tilstand altid med en reel probe mod egen origin,
 * og tilstanden gensynkroniseres ved mount og fokus.
 */
import { useCallback, useEffect, useState } from 'react';
import { WifiOff } from 'lucide-react';
import { useOfflineSync } from '../../hooks/useOfflineSync';

async function probeNetwork(): Promise<boolean> {
  try {
    await fetch(`/manifest.webmanifest?probe=${Date.now()}`, {
      method: 'HEAD',
      cache: 'no-store',
    });
    return true;
  } catch {
    return false;
  }
}

export function OfflineBanner() {
  const [isOnline, setIsOnline] = useState(true);
  const { pendingCount } = useOfflineSync();

  const syncState = useCallback(async () => {
    if (navigator.onLine) {
      setIsOnline(true);
      return;
    }
    // Påstået offline — bekræft med en rigtig request før banneret vises.
    setIsOnline(await probeNetwork());
  }, []);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', syncState);
    window.addEventListener('focus', syncState);
    document.addEventListener('visibilitychange', syncState);
    syncState();
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', syncState);
      window.removeEventListener('focus', syncState);
      document.removeEventListener('visibilitychange', syncState);
    };
  }, [syncState]);

  if (isOnline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-0 inset-x-0 z-[9990] flex items-center justify-center gap-2 bg-amber-500 px-4 py-2 text-sm font-medium text-white shadow-md"
      style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 8px)' }}
    >
      <WifiOff className="h-4 w-4 flex-shrink-0" aria-hidden />
      <span>
        Du er offline · Timer gemmes lokalt
        {pendingCount > 0 && ` (${pendingCount} afventende)`}
      </span>
    </div>
  );
}
